import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { ArrowLeft, Camera, CheckCircle2, Download, QrCode, RefreshCw, Save, X } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import Alert from '../../components/ui/Alert'
import QRScannerModal from '../../components/QRScannerModal'
import SignatureCanvasComponent, { type SignatureCanvasRef } from '../../components/SignatureCanvas'
import RemisionVolquetaPreview from './RemisionVolquetaPreview'
import { dumperHourmeterService, qrService, uploadService } from '../../services/dumperApi'
import { useAuth } from '../../contexts/AuthContext'
import { useActiveContext } from '../../hooks/useActiveContext'
import type { CreateDumperHourmeterPayload, DumperSource } from '../../types/dumper'
import type { MaterialAssetSummary } from '../../types'
import { routeChoice, routeLabel, zonesForRoute } from '../../utils/tripRoute'

interface FormState {
  realM3: string
  realTon: string
  tripCount: string
  remisionNumber: string
  materialType: string
  distanceKm: string
  photoUrl: string
  residentObservation: string
  loadingZone: string
  unloadingZone: string
  residentId: string
  newLicensePlate: string
  newCapacityM3: string
  // Override desde QR scan (cuando difiere del activeContext del residente)
  overrideDealId: string
  overrideLegacyOperatorId: string
  overrideOperationId: string
  overrideTransportAssetId: string
}

// RS-1338 v4: material info viene de asset_conditions role=MATERIAL del deal/op
// (MaterialAssetSummary). El transport asset ya tiene conditionMaterialId fijo
// — backend resuelve auto. Frontend solo muestra preview.

// Hora actual zona Bogotá en formato HH:mm
const bogotaTimeNow = () =>
  new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date())

// Fecha actual zona Bogotá en formato DD/MM/YYYY
const bogotaDateNow = () =>
  new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date())

// Fecha actual zona Bogotá en formato YYYY-MM-DD — para enviar al backend como
// fecha-calendario. Evita que un registro nocturno se corra al día siguiente al
// castear a DATE en UTC (no usar new Date().toISOString()).
const bogotaIsoDateNow = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())

const RegisterDumperTrip: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const editId = searchParams.get('editId')
  const initialQrCode = searchParams.get('qr')
  const { operator } = useAuth()
  const { context: activeContext, loading: contextLoading, refetch: refetchContext } = useActiveContext()
  const isResident = operator?.role === 'resident'
  const source: DumperSource = isResident ? 'RESIDENT_QR' : 'DRIVER'

  const [form, setForm] = useState<FormState>({
    realM3: '',
    realTon: '',
    tripCount: '1',
    remisionNumber: '',
    materialType: '',
    residentId: '',
    newLicensePlate: '',
    newCapacityM3: '',
    distanceKm: '',
    photoUrl: '',
    residentObservation: '',
    loadingZone: '',
    unloadingZone: '',
    overrideDealId: searchParams.get('dealId') || '',
    overrideLegacyOperatorId: searchParams.get('legacyOperatorId') || '',
    overrideOperationId: searchParams.get('operationId') || '',
    overrideTransportAssetId: searchParams.get('transportAssetId') || '',
  })
  const [qrResolved, setQrResolved] = useState<{
    dealId?: number | null
    operationId?: number | null
    transportAssetId?: number | null
    materialAssets?: MaterialAssetSummary[]
    licensePlate?: string | null
    nextRemisionNumber?: string | null
    dumperDealType?: 'TRANSPORT_ONLY' | 'MATERIAL_PLUS_TRANSPORT' | null
    // incomeUnit del asset elegido — N tarifas pueden tener distinta unidad.
    incomeUnit?: 'VIAJE' | 'M3' | 'M3_KM' | 'TONELADA' | 'HORA' | 'TONELADA_KM' | null
    transportUnit?: 'VIAJE' | 'M3' | 'M3_KM' | 'TONELADA' | 'HORA' | 'TONELADA_KM' | null
    materialUnit?: 'VIAJE' | 'M3' | 'M3_KM' | 'TONELADA' | 'HORA' | 'TONELADA_KM' | null
    // materialType del asset elegido — prefilla "Tipo material" en el form.
    materialType?: string | null
    // Grupo de condiciones del asset elegido (RS-1338).
    conditionIncomeName?: string | null
    conditionGroupId?: number | null
    groupName?: string | null
    photoRequired?: boolean
    // Residente fijo del asset (opcional) — si presente, prefilla y bloquea el picker.
    residentId?: number | null
    residentName?: string | null
    residentPhone?: string | null
    // Zonas pactadas en la TRANSPORT condition del asset — prefill del form.
    loadingZone?: string | null
    unloadingZone?: string | null
    assignedDriver?: {
      kind: 'saas' | 'legacy'
      saasOperatorId?: number
      legacyOperatorId?: number
      name: string
    } | null
  } | null>(null)
  // La ruta que cobra el viaje. Solo se pregunta cuando la obra tiene varias:
  // con una sola, esa ruta ES el grupo por defecto del equipo.
  const [routeId, setRouteId] = useState<number | null>(null)
  const [showScanner, setShowScanner] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [zoneOptions, setZoneOptions] = useState<{ loading: string[]; unloading: string[] }>({ loading: [], unloading: [] })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const driverSignatureRef = useRef<SignatureCanvasRef | null>(null)
  const [driverSignatureData, setDriverSignatureData] = useState<string | null>(null)
  const [submittedTrip, setSubmittedTrip] = useState<{ id: number } | null>(null)

  // Resolver el QR si vino en URL — para residente que escaneó volqueta no asignada.
  // RS-1338: una machinery puede tener N assets (tarifas). Si SelectAsset pasó
  // `pickedAsset` en state lo usamos directo; si no, matcheamos por
  // `transportAssetId` del query (override del picker) o caemos al primero.
  useEffect(() => {
    if (!initialQrCode) return
    qrService
      .resolve(initialQrCode)
      .then((res: any) => {
        if (res.registrationFlow !== 'DUMPER_HOURMETER') {
          setError('El QR no corresponde a una volqueta activa')
          return
        }
        const list: any[] = res.activeAssets ?? []
        const overrideId = Number(searchParams.get('transportAssetId')) || null
        const picked =
          (overrideId && list.find((a) => a.assetId === overrideId)) ||
          list[0] ||
          null
        if (picked) {
          setQrResolved({
            dealId: picked.asset?.dealId ?? null,
            operationId: picked.asset?.operationId ?? null,
            transportAssetId: picked.transportAssetId ?? picked.assetId ?? null,
            materialAssets: picked.materialAssets || [],
            licensePlate: res.licensePlate || null,
            nextRemisionNumber: picked.nextRemisionNumber || null,
            dumperDealType: picked.dumperDealType || null,
            incomeUnit: picked.incomeUnit || null,
            materialType: picked.materialType ?? null,
            conditionIncomeName: picked.conditionIncomeName ?? null,
            conditionGroupId: picked.conditionGroupId ?? null,
            groupName: picked.groupName ?? null,
            photoRequired: picked.photoRequired ?? false,
            residentId: picked.residentId ?? null,
            residentName: picked.residentName ?? null,
            residentPhone: picked.residentPhone ?? null,
            loadingZone: picked.loadingZone ?? null,
            unloadingZone: picked.unloadingZone ?? null,
            assignedDriver: picked.assignedDriver || null,
          })
          return
        }
        // Back-compat: backend viejo sin activeAssets[] → campos flat.
        setQrResolved({
          dealId: res.activeDeal?.dealId || null,
          operationId: res.activeOperation?.id || null,
          transportAssetId: res.transportAssetId || res.activeAsset?.id || null,
          materialAssets: res.materialAssets || [],
          licensePlate: res.licensePlate || null,
          nextRemisionNumber: res.nextRemisionNumber || null,
          dumperDealType: res.dumperDealType || null,
          conditionIncomeName: res.conditionIncomeName ?? null,
          conditionGroupId: res.conditionGroupId ?? null,
          groupName: res.groupName ?? null,
          photoRequired: res.photoRequired ?? false,
          assignedDriver: res.assignedDriver || null,
        })
      })
      .catch((e) => setError(e?.response?.data?.message || 'No se pudo resolver el QR'))
  }, [initialQrCode, searchParams])

  // Flujo dashboard (sin QR): SelectAsset pasó el asset elegido en location.state.
  // Usamos su hidratación exacta (nextRemisionNumber/materialAssets del asset
  // elegido, no del [0] de active-context).
  useEffect(() => {
    if (initialQrCode) return // flujo QR ya cubierto por el effect anterior
    const picked = (location.state as any)?.pickedAsset
    if (!picked) return
    setQrResolved({
      dealId: picked.dealId ?? null,
      operationId: picked.operationId ?? null,
      transportAssetId: picked.assetId ?? null,
      materialAssets: picked.materialAssets || [],
      licensePlate: picked.licensePlate || null,
      nextRemisionNumber: picked.nextRemisionNumber || null,
      dumperDealType: picked.dumperDealType || null,
      incomeUnit: picked.incomeUnit || null,
      materialType: picked.materialType ?? null,
      conditionIncomeName: picked.conditionIncomeName ?? null,
      conditionGroupId: picked.conditionGroupId ?? null,
      groupName: picked.groupName ?? null,
      photoRequired: picked.photoRequired ?? false,
      residentId: picked.residentId ?? null,
      residentName: picked.residentName ?? null,
      residentPhone: picked.residentPhone ?? null,
      loadingZone: picked.loadingZone ?? null,
      unloadingZone: picked.unloadingZone ?? null,
      assignedDriver: null,
    })
  }, [initialQrCode, location.state])

  // Combina activeContext + qrResolved + URL overrides → fuente única de verdad.
  // Para RESIDENTE el conductor se toma del QR (assignedDriver), NO del usuario logueado.
  // Para CONDUCTOR el conductor es el usuario logueado (saasOperatorId/legacyOperatorId del activeContext).
  // Las rutas de la obra y cuál arranca elegida: la que el aliado dejó
  // configurada para esa volqueta (`qrResolved` la trae por activo).
  const rutas = useMemo(
    () => routeChoice(
      activeContext?.kind === 'OPERATION' ? activeContext.routes : [],
      qrResolved?.conditionGroupId ?? null,
    ),
    [activeContext, qrResolved?.conditionGroupId],
  )

  useEffect(() => {
    if (!rutas.needsPick || routeId != null) return
    if (rutas.preselected != null) setRouteId(rutas.preselected)
  }, [rutas.needsPick, rutas.preselected, routeId])

  /**
   * Elegir la ruta impone SUS zonas: dejar escritas las de otra daría una
   * remisión que dice un recorrido y cobra otro.
   */
  const pickRoute = (id: number) => {
    setRouteId(id)
    const zonas = zonesForRoute(rutas.options.find((r) => r.id === id))
    if (zonas) setForm((p) => ({ ...p, ...zonas }))
  }

  const ctx = useMemo(() => {
    const dealId =
      Number(form.overrideDealId) ||
      qrResolved?.dealId ||
      (activeContext?.kind === 'DEAL' ? activeContext.dealId : null) ||
      null
    const operationId =
      Number(form.overrideOperationId) ||
      qrResolved?.operationId ||
      (activeContext?.kind === 'OPERATION' ? activeContext.operationId : null) ||
      null
    const transportAssetId =
      Number(form.overrideTransportAssetId) ||
      qrResolved?.transportAssetId ||
      (activeContext?.kind === 'OPERATION' ? activeContext.transportAssetId : null) ||
      (activeContext?.kind === 'DEAL' ? activeContext.transportAssetId ?? null : null) ||
      null
    const materialAssets: MaterialAssetSummary[] =
      qrResolved?.materialAssets ||
      (activeContext?.kind === 'DEAL' || activeContext?.kind === 'OPERATION'
        ? activeContext.materialAssets || []
        : [])

    // Driver: residente toma del QR.assignedDriver; conductor toma de su propio activeContext
    let legacyOperatorId: number | null = null
    let saasOperatorId: number | null = null
    let driverName: string | null = null
    if (isResident && qrResolved?.assignedDriver) {
      const ad = qrResolved.assignedDriver
      if (ad.kind === 'legacy') legacyOperatorId = ad.legacyOperatorId || null
      else if (ad.kind === 'saas') saasOperatorId = ad.saasOperatorId || null
      driverName = ad.name
    } else if (!isResident) {
      legacyOperatorId =
        Number(form.overrideLegacyOperatorId) ||
        (activeContext?.kind === 'DEAL' ? activeContext.legacyOperatorId || null : null) ||
        null
      saasOperatorId =
        activeContext?.kind === 'OPERATION' ? activeContext.saasOperatorId : null
      driverName = operator?.name || null
    }

    const dumperDealType =
      qrResolved?.dumperDealType ||
      (activeContext?.kind === 'OPERATION' || activeContext?.kind === 'DEAL'
        ? activeContext.dumperDealType
        : null) || null
    // incomeUnit del asset_condition INCOME vigente — determina columnas form.
    // Prioriza el asset elegido en el picker (qrResolved) sobre el flat de
    // active-context (que es del asset[0], puede no ser el elegido).
    const incomeUnit =
      qrResolved?.incomeUnit ??
      (activeContext?.kind === 'DEAL' ? activeContext.incomeUnit ?? null : null)
    const buildingName =
      activeContext?.kind === 'DEAL' ? activeContext.buildingName ?? null : null
    // También en una OBRA. Estaba fijo en el mundo ALQUILER, y el backend no
    // devolvía residentes de operación: el conductor de una obra veía el
    // selector vacío aunque su obra tuviera residente asignado.
    const residents =
      activeContext?.kind === 'DEAL' || activeContext?.kind === 'OPERATION'
        ? activeContext.residents || []
        : []
    // Residente FIJO del asset elegido (opcional). Si está, prefilla el form
    // y oculta el picker. Si null, el operario escoge del pool del deal.
    // Prioriza qrResolved (asset elegido) sobre transportAssets[0] del context.
    const fallbackTransportAsset =
      activeContext?.kind === 'OPERATION' || activeContext?.kind === 'DEAL'
        ? activeContext.transportAssets?.find((ta) => ta.assetId === transportAssetId)
          || activeContext.transportAssets?.[0]
          || null
        : null
    const assetResidentId =
      qrResolved?.residentId
      ?? fallbackTransportAsset?.effectiveResidentId
      ?? fallbackTransportAsset?.residentId
      ?? null
    const assetResidentName =
      qrResolved?.residentName
      ?? fallbackTransportAsset?.effectiveResidentName
      ?? fallbackTransportAsset?.residentName
      ?? null
    const assetResidentPhone =
      qrResolved?.residentPhone
      ?? fallbackTransportAsset?.effectiveResidentPhone
      ?? fallbackTransportAsset?.residentPhone
      ?? null
    // materialType del asset elegido — prefilla el form.
    const assetMaterialType =
      qrResolved?.materialType ?? fallbackTransportAsset?.materialType ?? null
    // Zonas pactadas en la TRANSPORT condition del asset — prefill del form.
    // Si null, el form arranca vacío (autocomplete histórico sigue activo).
    const assetLoadingZone =
      qrResolved?.loadingZone ?? fallbackTransportAsset?.loadingZone ?? null
    const assetUnloadingZone =
      qrResolved?.unloadingZone ?? fallbackTransportAsset?.unloadingZone ?? null
    // Units de TRANSPORT/MATERIAL del asset elegido — determinan visibilidad del form.
    // realM3 y tripCount siempre van; distanceKm solo si alguna unit=M3_KM.
    const transportUnit =
      qrResolved?.transportUnit ?? fallbackTransportAsset?.transportUnit ?? null
    const materialUnit =
      qrResolved?.materialUnit ?? fallbackTransportAsset?.materialUnit ?? null
    // Distintos materialTypes accesibles al operador (todos sus transportAssets).
    // Si >1 distintos → el form muestra select; si <=1 → input freetext.
    const operatorTransportAssets =
      (activeContext?.kind === 'OPERATION' || activeContext?.kind === 'DEAL'
        ? activeContext.transportAssets
        : null) || []
    const availableMaterialTypes = Array.from(
      new Set(
        operatorTransportAssets
          .map((ta) => ta.materialType)
          .filter((m): m is string => Boolean(m))
      )
    )
    const driverSignatureUrl =
      activeContext?.kind === 'DEAL' ? activeContext.driverSignatureUrl ?? null : null
    const capacityM3 =
      activeContext?.kind === 'DEAL' ? activeContext.capacityM3 ?? null : null
    const licensePlate =
      qrResolved?.licensePlate ??
      (activeContext?.kind === 'OPERATION' || activeContext?.kind === 'DEAL'
        ? activeContext.licensePlate
        : null)
    // Grupo de condiciones del asset elegido — foto obligatoria + nombre compuesto.
    const photoRequired =
      qrResolved?.photoRequired ?? fallbackTransportAsset?.photoRequired ?? false
    const groupName =
      qrResolved?.groupName ??
      fallbackTransportAsset?.groupName ??
      qrResolved?.conditionIncomeName ??
      fallbackTransportAsset?.conditionIncomeName ??
      null
    const suggestedRemision =
      qrResolved?.nextRemisionNumber ??
      (activeContext?.kind === 'OPERATION' || activeContext?.kind === 'DEAL'
        ? activeContext.nextRemisionNumber
        : null)
    return {
      dealId,
      operationId,
      transportAssetId,
      materialAssets,
      legacyOperatorId,
      saasOperatorId,
      driverName,
      dumperDealType,
      licensePlate,
      suggestedRemision,
      incomeUnit,
      transportUnit,
      materialUnit,
      buildingName,
      residents,
      assetResidentId,
      assetResidentName,
      assetResidentPhone,
      assetMaterialType,
      assetLoadingZone,
      assetUnloadingZone,
      availableMaterialTypes,
      driverSignatureUrl,
      capacityM3,
      photoRequired,
      groupName,
    }
  }, [activeContext, qrResolved, isResident, operator?.name, form.overrideDealId, form.overrideLegacyOperatorId, form.overrideOperationId, form.overrideTransportAssetId])

  // Prefill remisión sugerida
  useEffect(() => {
    if (ctx.suggestedRemision && !form.remisionNumber) {
      setForm((p) => ({ ...p, remisionNumber: ctx.suggestedRemision || '' }))
    }
  }, [ctx.suggestedRemision])

  // Prefill zonas desde la TRANSPORT condition del asset elegido. Solo aplica
  // si el form está vacío — el driver puede sobrescribir y no lo pisamos.
  // Re-aplica si cambia el asset (otro picker → otras zonas pactadas).
  useEffect(() => {
    if (ctx.assetLoadingZone && !form.loadingZone) {
      setForm((p) => ({ ...p, loadingZone: ctx.assetLoadingZone! }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.assetLoadingZone])
  useEffect(() => {
    if (ctx.assetUnloadingZone && !form.unloadingZone) {
      setForm((p) => ({ ...p, unloadingZone: ctx.assetUnloadingZone! }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.assetUnloadingZone])

  // Zonas históricas del deal/operación (autocomplete sugerido en form)
  useEffect(() => {
    if (!ctx.dealId && !ctx.operationId) return
    dumperHourmeterService
      .getZones({ dealId: ctx.dealId, operationId: ctx.operationId })
      .then(setZoneOptions)
      .catch(() => setZoneOptions({ loading: [], unloading: [] }))
  }, [ctx.dealId, ctx.operationId])

  // RS-1338 v4: material auto-resuelto en backend via transportAsset.conditionMaterialId.
  // Frontend NO selecciona — solo muestra preview cuando applies.

  // Pre-select del residente (solo si el form está vacío — no piso override manual):
  //  1) Si el asset tiene residente fijo → ese.
  //  2) Si no, y el pool del deal tiene 1 solo → ese.
  // El driver puede cambiar después desde el select.
  useEffect(() => {
    if (form.residentId) return
    if (ctx.assetResidentId) {
      setForm((p) => ({ ...p, residentId: String(ctx.assetResidentId) }))
      return
    }
    if (ctx.residents?.length === 1) {
      setForm((p) => ({ ...p, residentId: String(ctx.residents![0].residentId) }))
    }
  }, [ctx.assetResidentId, ctx.residents])

  // Pre-fill "Tipo material" con el materialType del asset elegido.
  // Solo si el form está vacío (no pisar override manual del driver). Cuando el
  // operario cambia de asset (picker), assetMaterialType cambia y se reaplica.
  useEffect(() => {
    if (ctx.assetMaterialType && !form.materialType) {
      setForm((p) => ({ ...p, materialType: ctx.assetMaterialType! }))
    }
  }, [ctx.assetMaterialType])

  // Edit mode: load existing
  useEffect(() => {
    if (!editId) return
    const id = Number(editId)
    dumperHourmeterService
      .findOne(id)
      .then((t) => {
        setForm((prev) => ({
          ...prev,
          remisionNumber: t.remisionNumber,
          realM3: String(t.realM3),
          realTon: t.realTon != null ? String(t.realTon) : '',
          materialType: t.materialType || '',
          distanceKm: t.distanceKm != null ? String(t.distanceKm) : '',
          photoUrl: t.photoUrl || '',
          residentObservation: t.residentObservation || '',
        }))
      })
      .catch((e) => setError(e?.response?.data?.message || 'No se pudo cargar viaje a editar'))
  }, [editId])

  const handlePhotoSelected = async (file: File | null) => {
    if (!file) return
    setError(null)
    setUploadingPhoto(true)
    try {
      const { location } = await uploadService.uploadFile(file)
      setForm((p) => ({ ...p, photoUrl: location }))
      setInfo('Foto subida')
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error subiendo foto')
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handleScanResult = async (qrText: string) => {
    setShowScanner(false)
    try {
      const res = (await qrService.resolve(qrText)) as any
      if (res.registrationFlow !== 'DUMPER_HOURMETER') {
        setError('El QR no corresponde a una volqueta activa')
        return
      }
      // RS-1338: una machinery puede tener N tarifas (assets). Si hay >1 hay
      // que llevar al picker — antes esta función tomaba activeAsset[0]
      // silenciosamente y el driver registraba con tarifa equivocada.
      const list: any[] = (res.activeAssets ?? []).filter((qa: any) => qa.isVolqueta)
      if (list.length > 1) {
        const options = list.map((qa: any) => ({
          assetId: qa.assetId,
          conditionIncomeName: qa.conditionIncomeName,
          incomeRate: qa.incomeRate,
          incomeUnit: qa.incomeUnit,
          dumperDealType: qa.dumperDealType,
          nextRemisionNumber: qa.nextRemisionNumber,
          materialAssets: qa.materialAssets ?? [],
          licensePlate: res.licensePlate ?? null,
          dealId: qa.asset?.dealId ?? null,
          operationId: qa.asset?.operationId ?? null,
          residentId: qa.residentId ?? null,
          residentName: qa.residentName ?? null,
          residentPhone: qa.residentPhone ?? null,
          materialType: qa.materialType ?? null,
        }))
        navigate('/dumper-hourmeters/select-asset', {
          state: { assets: options, qrCode: qrText },
        })
        return
      }
      const picked = list[0] || null
      setQrResolved({
        dealId: picked?.asset?.dealId ?? res.activeDeal?.dealId ?? null,
        operationId: picked?.asset?.operationId ?? res.activeOperation?.id ?? null,
        transportAssetId: picked?.transportAssetId ?? picked?.assetId ?? res.transportAssetId ?? res.activeAsset?.id ?? null,
        materialAssets: picked?.materialAssets ?? res.materialAssets ?? [],
        licensePlate: res.licensePlate ?? null,
        nextRemisionNumber: picked?.nextRemisionNumber ?? res.nextRemisionNumber ?? null,
        dumperDealType: picked?.dumperDealType ?? res.dumperDealType ?? null,
        incomeUnit: picked?.incomeUnit ?? null,
        materialType: picked?.materialType ?? null,
        residentId: picked?.residentId ?? null,
        residentName: picked?.residentName ?? null,
        residentPhone: picked?.residentPhone ?? null,
        assignedDriver: picked?.assignedDriver ?? res.assignedDriver ?? null,
      })
      setInfo('Datos cargados desde el QR')
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo resolver el QR')
    }
  }

  const update = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }))

  const handleSubmit = async () => {
    setError(null)
    setInfo(null)
    setSubmitting(true)
    try {
      if (!ctx.dealId && !ctx.operationId) {
        throw new Error('Sin contexto activo: el operario no tiene volqueta asignada')
      }
      // Validación según units de las 3 conditions (income/transport/material).
      // Cada qty se valida si AL MENOS UNA condition la requiere.
      const allUnits = [ctx.incomeUnit, ctx.transportUnit, ctx.materialUnit].filter(Boolean) as string[]
      // HORA no es una unidad de viaje: `dumper_hourmeters` no tiene columna de
      // horas y el backend la trata como fuera de alcance, así que el viaje se
      // registraría sin cantidad y liquidaría en $0. Es una tarifa mal
      // configurada — se dice, no se registra.
      if (ctx.incomeUnit === 'HORA') {
        throw new Error(
          'Esta volqueta tiene tarifa por hora. Una volqueta se cobra por viaje o m³ — '
          + 'corrige la tarifa de la obra antes de registrar viajes.'
        )
      }
      const requires = {
        realM3: allUnits.some((u) => u === 'M3' || u === 'M3_KM'),
        realTon: allUnits.some((u) => u === 'TONELADA' || u === 'TONELADA_KM'),
        tripCount: allUnits.some((u) => u === 'VIAJE'),
        distanceKm: allUnits.some((u) => u === 'M3_KM' || u === 'TONELADA_KM'),
      }
      if (requires.realM3 && !form.realM3) {
        throw new Error('m³ reales requerido por tarifa M3 o M3_KM')
      }
      if (requires.realTon && !form.realTon) {
        throw new Error('Toneladas requeridas por tarifa TONELADA o TONELADA_KM')
      }
      if (requires.distanceKm && !form.distanceKm) {
        throw new Error('Distancia (km) requerida por tarifa M3_KM o TONELADA_KM')
      }
      if (requires.tripCount) {
        const tc = Number(form.tripCount)
        if (!Number.isInteger(tc) || tc < 1) {
          throw new Error('Número de viajes debe ser un entero ≥ 1')
        }
      }
      const remisionNumber = form.remisionNumber || ctx.suggestedRemision || ''
      if (!remisionNumber) throw new Error('No se pudo generar número de remisión')

      // Driver flow: residente obligatorio. signMode siempre OTP (residente firma vía código).
      if (source === 'DRIVER') {
        if (!form.residentId) throw new Error('Selecciona el residente que recibirá la firma')
        // Firma del driver: si no tiene guardada, debe dibujarla esta vez
        if (!ctx.driverSignatureUrl && !driverSignatureData) {
          throw new Error('Dibuja tu firma — solo se pide la primera vez, se reusará en próximos viajes')
        }
        // Placa obligatoria: si no hay stored ni ingresada, bloquear
        if (!ctx.licensePlate && !form.newLicensePlate.trim()) {
          throw new Error('Ingresa la placa de la volqueta — no se puede registrar el viaje sin placa')
        }
      }

      // Foto obligatoria por grupo de condiciones (RS-1338). Aplica a todos los
      // flujos; el backend también valida (412) como defensa en profundidad.
      if (ctx.photoRequired && !form.photoUrl) {
        throw new Error('La foto es obligatoria para este grupo de condiciones')
      }

      const payload: CreateDumperHourmeterPayload = {
        source,
        tripDate: bogotaIsoDateNow(),
        remisionNumber,
      }
      payload.realM3 = form.realM3 ? Number(form.realM3) : 0
      if (form.realTon) payload.realTon = Number(form.realTon)
      if (requires.tripCount || ctx.incomeUnit === 'VIAJE') {
        payload.tripCount = Number(form.tripCount) || 1
      }
      if (ctx.dealId) {
        if (!ctx.legacyOperatorId) {
          throw new Error('Falta legacyOperatorId para path Retri-managed')
        }
        if (!ctx.transportAssetId) {
          throw new Error('Deal sin asset de transporte configurado. Configura el deal en website antes de registrar viajes.')
        }
        payload.dealId = ctx.dealId
        payload.transportAssetId = ctx.transportAssetId
        payload.legacyOperatorId = ctx.legacyOperatorId
      } else if (ctx.operationId) {
        if (!ctx.transportAssetId || !ctx.saasOperatorId) {
          throw new Error('Falta transportAssetId/operatorId para path SaaS')
        }
        payload.operationId = ctx.operationId
        payload.transportAssetId = ctx.transportAssetId
        payload.operatorId = ctx.saasOperatorId
      }

      // RS-1338 v4: material resuelto en backend via transportAsset.conditionMaterialId.
      // Frontend solo envía materialType freetext si el driver lo capturó (override visual).
      if (ctx.dumperDealType === 'MATERIAL_PLUS_TRANSPORT' && form.materialType) {
        payload.materialType = form.materialType
      }
      if (form.distanceKm) payload.distanceKm = Number(form.distanceKm)
      if (form.photoUrl) payload.photoUrl = form.photoUrl
      if (form.residentObservation) payload.residentObservation = form.residentObservation
      // La ruta que se cobra. Sin ella el backend usa el grupo por defecto del
      // equipo, que es el caso de la obra de una sola ruta.
      if (routeId != null) payload.conditionGroupId = routeId
      if (form.loadingZone) payload.loadingZone = form.loadingZone
      if (form.unloadingZone) payload.unloadingZone = form.unloadingZone
      if (source === 'DRIVER') {
        payload.signMode = 'IN_PERSON'
        payload.residentId = Number(form.residentId)
        // Si dibujó firma nueva, subir y mandar URL para persistir en operator
        if (driverSignatureData) {
          const blob = await (await fetch(driverSignatureData)).blob()
          const file = new File([blob], `driver-signature-${Date.now()}.png`, { type: 'image/png' })
          const { location } = await uploadService.uploadFile(file)
          payload.driverSignatureUrl = location
        }
        // Si la machinery no tenía placa/capacidad y el driver las ingresó, persistir
        if (!ctx.licensePlate && form.newLicensePlate.trim()) {
          payload.machineryLicensePlate = form.newLicensePlate.trim()
        }
        if (ctx.capacityM3 == null && form.newCapacityM3) {
          payload.machineryCubicCapacityM3 = Number(form.newCapacityM3)
        }
      }
      if (source === 'RESIDENT_QR' && operator?.id) {
        payload.registeredByResidentId = operator.id
      }

      const created = editId
        ? await dumperHourmeterService.update(Number(editId), payload as any)
        : await dumperHourmeterService.create(payload)
      setInfo('Viaje registrado correctamente')
      if (editId) {
        navigate(`/dumper-hourmeters/${created.id}`)
      } else {
        // Success view inline con preview + acciones (RS-1338 feedback #4)
        setSubmittedTrip({ id: created.id })
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'No se pudo registrar el viaje')
    } finally {
      setSubmitting(false)
    }
  }

  if (contextLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-sm text-gray-600">Cargando contexto…</p>
      </div>
    )
  }

  const noContext = !ctx.dealId && !ctx.operationId
  const isMaterial = ctx.dumperDealType === 'MATERIAL_PLUS_TRANSPORT'
  // Bloqueante: deal pide material pero no hay conditions role=MATERIAL cargadas.
  // Backend devolvería 412; bloqueamos pre-submit para mejor UX.
  const missingMaterialConfig = isMaterial && (ctx.materialAssets?.length ?? 0) === 0

  const handleStartAnother = async () => {
    setSubmittedTrip(null)
    setInfo(null)
    setError(null)
    setDriverSignatureData(null)
    setForm((p) => ({
      ...p,
      realM3: '',
      realTon: '',
      tripCount: '1',
      distanceKm: '',
      photoUrl: '',
      residentObservation: '',
      remisionNumber: '',
      newLicensePlate: '',
      newCapacityM3: '',
      // Conserva lo que el driver escribió en el trip previo — si manualmente
      // puso "Cantera ABC", queremos que persista para el siguiente. Solo
      // caemos al ctx pactado cuando el driver dejó el campo vacío.
      loadingZone: p.loadingZone || ctx.assetLoadingZone || '',
      unloadingZone: p.unloadingZone || ctx.assetUnloadingZone || '',
      materialType: p.materialType || ctx.assetMaterialType || '',
    }))
    // Refetch para obtener nextRemisionNumber actualizado (evita colisión D{deal}-{seq})
    await refetchContext()
  }

  const handleDownloadActa = async () => {
    if (!submittedTrip) return
    try {
      await dumperHourmeterService.downloadActa(submittedTrip.id)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo generar el PDF')
    }
  }

  if (submittedTrip) {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-retri-blue px-3 py-3 flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-white">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-white font-semibold">Viaje registrado</h1>
        </header>
        <main className="max-w-2xl mx-auto p-4 space-y-4">
          <Alert variant="success">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>Viaje #{submittedTrip.id} registrado. Pasa el dispositivo al residente para que firme.</span>
            </div>
          </Alert>
          <RemisionVolquetaPreview
            form={form}
            ctx={{
              licensePlate: ctx.licensePlate || form.newLicensePlate || null,
              driverName: ctx.driverName,
              dealId: ctx.dealId,
              suggestedRemision: ctx.suggestedRemision,
              buildingName: ctx.buildingName,
            }}
            materialAssetSelected={ctx.materialAssets.length === 1 ? ctx.materialAssets[0] : null}
            currentDate={bogotaDateNow()}
            currentTime={bogotaTimeNow()}
            driverSignatureDataUrl={driverSignatureData}
          />
          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={handleDownloadActa} variant="outline">
              <Download className="w-4 h-4 mr-2" /> Descargar acta PDF
            </Button>
            <Button onClick={handleStartAnother}>Seguir registrando</Button>
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>Volver al inicio</Button>
          </div>
        </main>
      </div>
    )
  }

  // Combina residente fijo del asset + pool del deal — el driver puede cambiar
  // entre ellos. El elegido (form.residentId) es el que se manda a dumper_hourmeter.
  const residentOptions: Array<{
    residentId: number
    name: string
    phone?: string
    fixed?: boolean
  }> = []
  if (ctx.assetResidentId) {
    residentOptions.push({
      residentId: ctx.assetResidentId,
      name: ctx.assetResidentName || `#${ctx.assetResidentId}`,
      phone: ctx.assetResidentPhone || undefined,
      fixed: true,
    })
  }
  for (const r of ctx.residents || []) {
    if (r.residentId !== ctx.assetResidentId) {
      residentOptions.push({ residentId: r.residentId, name: r.name, phone: r.phone })
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-retri-blue px-3 py-3 flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-white font-semibold">
          {editId ? 'Editar recibo' : 'Crear recibo de volqueta'}
        </h1>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-4">
        {info && <Alert variant="success">{info}</Alert>}
        {error && <Alert variant="error">{error}</Alert>}
        {noContext && (
          <Alert variant="error">
            Sin contexto activo. {isResident
              ? 'Escanea el QR de la volqueta.'
              : 'Tu volqueta no está asignada a un negocio activo.'}
          </Alert>
        )}
        {missingMaterialConfig && (
          <Alert variant="error">
            El deal requiere material pero no hay proveedor configurado. Contacta a Retri admin
            antes de registrar el viaje.
          </Alert>
        )}
        {/* Driver sin residentes asignados — sin esto el bloque del form no se
            renderiza y el user queda sin manera de pedir refresh manual.
            Si el asset tiene residente fijo no aplica este warning. */}
        {!isResident && !noContext && !ctx.assetResidentId && (ctx.residents?.length ?? 0) === 0 && (
          <Alert variant="warning">
            <div className="flex items-center justify-between gap-2">
              <span>El deal no tiene residentes asignados. Pide a Retri admin que asigne uno y refresca.</span>
              <button
                type="button"
                onClick={() => refetchContext()}
                className="text-xs text-retri-blue hover:underline flex items-center gap-1 shrink-0"
              >
                <RefreshCw className="w-3 h-3" /> Actualizar
              </button>
            </div>
          </Alert>
        )}

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400">Fecha</p>
                <p className="font-semibold text-gray-900">{bogotaDateNow()}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400">Hora (Bogotá)</p>
                <p className="font-semibold text-gray-900">{bogotaTimeNow()}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400">Placa</p>
                <p className="font-semibold text-gray-900">
                  {ctx.licensePlate || <span className="text-gray-400">—</span>}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400">
                  {isResident ? 'Conductor (volqueta)' : 'Operario'}
                </p>
                <p className="font-semibold text-gray-900">
                  {ctx.driverName || <span className="text-gray-400">—</span>}
                </p>
              </div>
              {ctx.groupName && (
                <div className="col-span-2">
                  <p className="text-xs uppercase tracking-wide text-gray-400">Grupo de condiciones</p>
                  <p className="font-semibold text-gray-900 flex items-center gap-2">
                    {ctx.groupName}
                    {ctx.photoRequired && (
                      <span className="text-[11px] font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">
                        Foto obligatoria
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 pt-3 space-y-3">
              {isResident && (
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setShowScanner(true)}>
                    <QrCode className="w-4 h-4 mr-2" /> Escanear QR
                  </Button>
                </div>
              )}

              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400">Número de remisión</p>
                <p className="font-semibold text-gray-900">
                  {form.remisionNumber || ctx.suggestedRemision || <span className="text-gray-400">—</span>}
                </p>
              </div>

              {/* Si la volqueta no tiene placa registrada, pedirla — required para firmar */}
              {!ctx.licensePlate && !isResident && (
                <Input
                  label="Placa de la volqueta *"
                  value={form.newLicensePlate}
                  onChange={update('newLicensePlate')}
                  placeholder="Ej: ABC-123"
                  helpText="La volqueta no tiene placa registrada. Se guardará para próximos viajes."
                  required
                />
              )}

              {/* Si la volqueta no tiene capacidad m³ registrada, pedirla */}
              {ctx.capacityM3 == null && !isResident && (
                <Input
                  label="Capacidad m³ de la volqueta"
                  type="number"
                  step="0.01"
                  value={form.newCapacityM3}
                  onChange={update('newCapacityM3')}
                  placeholder="Ej: 17"
                  helpText="Capacidad máxima de carga. Se guardará para próximos viajes."
                />
              )}

              {/* Visibilidad unit-aware: las 3 conditions (income/transport/material)
                  pueden tener units distintas. realM3 y tripCount SIEMPRE van.
                  distanceKm aparece solo si alguna unit del asset = M3_KM.
                  required se evalúa por cada unit que efectivamente lo use. */}
              {(() => {
                const units = [ctx.incomeUnit, ctx.transportUnit, ctx.materialUnit].filter(Boolean) as string[]
                const needsRealM3 = units.some((u) => u === 'M3' || u === 'M3_KM')
                const needsRealTon = units.some((u) => u === 'TONELADA' || u === 'TONELADA_KM')
                const needsTrips = units.some((u) => u === 'VIAJE')
                const needsDistance = units.some((u) => u === 'M3_KM' || u === 'TONELADA_KM')
                return (
                  <>
                    {/* Si NO hay M3-based pero igual hay TONELADA, mostramos m³ opcional
                        (puede coexistir cuando una condition pacta m³ y otra ton). */}
                    {(needsRealM3 || !needsRealTon) && (
                      <Input
                        label="m³ reales"
                        type="number"
                        step="0.01"
                        value={form.realM3}
                        onChange={update('realM3')}
                        required={needsRealM3}
                      />
                    )}
                    {needsRealTon && (
                      <Input
                        label="Toneladas reales"
                        type="number"
                        step="0.01"
                        value={form.realTon}
                        onChange={update('realTon')}
                        required
                      />
                    )}
                    {needsDistance && (
                      <Input
                        label="Distancia (km)"
                        type="number"
                        step="0.01"
                        value={form.distanceKm}
                        onChange={update('distanceKm')}
                        required
                      />
                    )}
                    <Input
                      label="Número de viajes"
                      type="number"
                      step="1"
                      min="1"
                      value={form.tripCount}
                      onChange={update('tripCount')}
                      required={needsTrips}
                    />
                  </>
                )
              })()}

              {/* RS-1338 v4: material asignado al asset transporte (read-only).
                  Backend resuelve via transportAsset.conditionMaterialId.
                  Cuando el deal tiene varios proveedores, NO sabemos cuál usa
                  este asset (backend no expone conditionMaterialId aún en
                  active-context). Mostramos mensaje neutro para no confundir. */}
              {isMaterial && ctx.materialAssets && ctx.materialAssets.length === 1 && (
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-wide text-gray-400">
                    Material configurado
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {ctx.materialAssets[0].supplierCompanyName || `Proveedor #${ctx.materialAssets[0].id}`}
                    {ctx.materialAssets[0].materialType ? ` — ${ctx.materialAssets[0].materialType}` : ''}
                  </p>
                </div>
              )}
              {/* "Tipo material" — preseleccionado con el del asset elegido.
                  Si el operador tiene >1 materialType distinto entre sus assets
                  → select para escoger entre ellos. Si <=1 → input freetext (override). */}
              {isMaterial && (ctx.materialAssets?.length ?? 0) > 0 && (
                ctx.availableMaterialTypes.length > 1 ? (
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-600">
                      Tipo material
                    </label>
                    <select
                      className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                      value={form.materialType}
                      onChange={(e) => setForm((p) => ({ ...p, materialType: e.target.value }))}
                    >
                      {ctx.availableMaterialTypes.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-gray-500">
                      Preseleccionado según la volqueta. Cambialo si el viaje fue de otro material.
                    </p>
                  </div>
                ) : (
                  <Input
                    label="Tipo material (opcional, override)"
                    value={form.materialType}
                    onChange={update('materialType')}
                    placeholder="AFIRMADO, ARENA, SUBBASE…"
                  />
                )
              )}

              {rutas.needsPick && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-900">Ruta *</p>
                  <p className="text-xs text-gray-500">
                    Esta obra cobra distinto según el recorrido. Elige el que hiciste.
                  </p>
                  <div className="grid gap-2">
                    {rutas.options.map((r) => {
                      const activa = routeId === r.id
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => pickRoute(r.id)}
                          className={`rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                            activa
                              ? 'border-retri-blue bg-blue-50 font-medium text-retri-blue'
                              : 'border-gray-200 text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <span className="block">{routeLabel(r)}</span>
                          {r.incomeRate != null && (
                            <span className="block text-xs text-gray-500">
                              {Number(r.incomeRate).toLocaleString('es-CO')}
                              {r.incomeUnit ? ` por ${r.incomeUnit}` : ''}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Zonas: autocomplete con histórico del deal — driver puede elegir o crear */}
              <Input
                label="Zona de cargue"
                value={form.loadingZone}
                onChange={update('loadingZone')}
                placeholder="Cantera, sector, dirección…"
                list="loading-zone-options"
                autoComplete="off"
              />
              <datalist id="loading-zone-options">
                {zoneOptions.loading.map((z) => (
                  <option key={`l-${z}`} value={z} />
                ))}
              </datalist>
              <Input
                label="Zona de descargue"
                value={form.unloadingZone}
                onChange={update('unloadingZone')}
                placeholder="Obra, sector, dirección…"
                list="unloading-zone-options"
                autoComplete="off"
              />
              <datalist id="unloading-zone-options">
                {zoneOptions.unloading.map((z) => (
                  <option key={`u-${z}`} value={z} />
                ))}
              </datalist>

              {/* Residente que firma — un solo select que combina el residente
                  FIJO del asset (si tiene) + el pool del deal. El driver puede
                  cambiar entre ellos por si hoy firma otro. El form.residentId
                  es el que se manda al dumper_hourmeter. */}
              {!isResident && residentOptions.length > 0 && (
                <div className="space-y-3 border-t border-gray-200 pt-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-medium text-gray-600">
                      Residente que firma *
                    </label>
                    <button
                      type="button"
                      onClick={() => refetchContext()}
                      className="text-[10px] text-retri-blue hover:underline flex items-center gap-1"
                      title="Refrescar lista de residentes"
                    >
                      <RefreshCw className="w-3 h-3" /> Actualizar
                    </button>
                  </div>
                  <select
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                    value={form.residentId}
                    onChange={(e) => setForm((p) => ({ ...p, residentId: e.target.value }))}
                    required
                  >
                    <option value="">Seleccionar residente…</option>
                    {residentOptions.map((r) => (
                      <option key={r.residentId} value={r.residentId}>
                        {r.name}{r.phone ? ` — ${r.phone}` : ''}{r.fixed ? ' · fijo del asset' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-gray-500">
                    Preseleccionado el del asset. Cambialo si hoy firma otro residente del deal.
                  </p>
                </div>
              )}

              {/* Firma del driver — separada del residente para que se vea
                  siempre que hay alguien con quien firmar. Solo primera vez;
                  después se reusa automáticamente desde operators.signatureUrl. */}
              {!isResident && residentOptions.length > 0 && (
                <div className="space-y-1 border-t border-gray-200 pt-3">
                  {ctx.driverSignatureUrl ? (
                    <>
                      <label className="block text-xs font-medium text-gray-600">Tu firma (guardada)</label>
                      <img
                        src={ctx.driverSignatureUrl}
                        alt="Firma guardada"
                        className="border border-gray-300 rounded bg-white p-2 max-h-20"
                      />
                      <p className="text-[10px] text-gray-500">
                        Tu firma se reusa automáticamente en cada viaje.
                      </p>
                    </>
                  ) : (
                    <>
                      <label className="block text-xs font-medium text-gray-600">
                        Tu firma * <span className="text-gray-400">(solo esta vez — se guarda para próximos viajes)</span>
                      </label>
                      <SignatureCanvasComponent
                        ref={driverSignatureRef}
                        onSignatureChange={setDriverSignatureData}
                      />
                    </>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Foto remisión {ctx.photoRequired ? '(obligatoria)' : '(opcional)'}
                </label>
                {form.photoUrl ? (
                  <div className="relative inline-block">
                    <img
                      src={form.photoUrl}
                      alt="Remisión"
                      className="w-40 h-40 object-cover rounded border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, photoUrl: '' }))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow"
                      aria-label="Quitar foto"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => cameraInputRef.current?.click()}
                      disabled={uploadingPhoto}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      {uploadingPhoto ? 'Subiendo…' : 'Tomar foto'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                    >
                      Subir archivo
                    </Button>
                  </div>
                )}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handlePhotoSelected(e.target.files?.[0] || null)}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handlePhotoSelected(e.target.files?.[0] || null)}
                />
              </div>

              {isResident && (
                <Input
                  label="Observación residente"
                  value={form.residentObservation}
                  onChange={update('residentObservation')}
                />
              )}
            </div>

            <Button
              variant="primary"
              size="lg"
              className="w-full mt-2"
              onClick={handleSubmit}
              disabled={submitting || noContext || uploadingPhoto || missingMaterialConfig}
            >
              <Save className="w-4 h-4 mr-2" />
              {submitting ? 'Guardando…' : editId ? 'Actualizar recibo' : 'Crear recibo'}
            </Button>
          </CardContent>
        </Card>

        {!noContext && (
          <RemisionVolquetaPreview
            form={form}
            ctx={{
              licensePlate: ctx.licensePlate,
              driverName: ctx.driverName,
              dealId: ctx.dealId,
              suggestedRemision: ctx.suggestedRemision,
              buildingName: ctx.buildingName,
              driverSignatureUrl: ctx.driverSignatureUrl,
            }}
            materialAssetSelected={ctx.materialAssets.length === 1 ? ctx.materialAssets[0] : null}
            currentDate={bogotaDateNow()}
            currentTime={bogotaTimeNow()}
            driverSignatureDataUrl={driverSignatureData}
          />
        )}
      </main>

      <QRScannerModal
        open={showScanner}
        onClose={() => setShowScanner(false)}
        onDecode={handleScanResult}
      />
    </div>
  )
}

export default RegisterDumperTrip
